import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useDelivererAuthStore } from './auth';

export interface DelivererProfile {
    id: string;
    name: string;
    phone: string;
    email: string | null;
    email_is_synthetic: boolean;
    photo_url: string | null;
    vehicle_type: string | null;
    license_plate: string | null;
    status: 'available' | 'unavailable' | 'busy';
    initials: string;
}

const STORAGE_KEY = 'pwa_deliverer_token';

function authHeader(): Record<string, string> {
    const token = localStorage.getItem(STORAGE_KEY);
    return token ? { Authorization: `Bearer ${token}`, Accept: 'application/json' } : { Accept: 'application/json' };
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(path, {
        method,
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error((err as { message?: string }).message ?? 'Request failed'), { status: res.status });
    }
    return res.json() as Promise<T>;
}

export const useDelivererStore = defineStore('deliverer', () => {
    const profile = ref<DelivererProfile | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const statusUpdating = ref(false);
    const pendingCount = ref(0);

    async function fetchProfile(): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            profile.value = await apiFetch<DelivererProfile>('GET', '/api/v1/deliverer/me');
        } catch (e: unknown) {
            const status = (e as { status?: number })?.status;
            if (status === 401) {
                // Token guardado no localStorage não é mais válido (expirado/revogado) —
                // desloga para o watch em App.vue redirecionar pro /login.
                useDelivererAuthStore().logout();
                return;
            }
            error.value = (e as { message?: string })?.message ?? 'Erro ao carregar perfil.';
            console.error('[deliverer/me]', e);
        } finally {
            loading.value = false;
        }
    }

    async function setStatus(status: 'available' | 'unavailable'): Promise<void> {
        if (!profile.value) return;
        statusUpdating.value = true;
        try {
            profile.value = await apiFetch<DelivererProfile>('PATCH', '/api/v1/deliverer/me/status', { status });
        } finally {
            statusUpdating.value = false;
        }
    }

    async function updateProfile(payload: { name?: string; email?: string | null; vehicle_type?: string; license_plate?: string | null }): Promise<void> {
        const updated = await apiFetch<DelivererProfile>('PATCH', '/api/v1/deliverer/me', payload);
        profile.value = updated;
    }

    async function updatePhoto(file: File): Promise<string> {
        const form = new FormData();
        form.append('photo', file);
        const token = localStorage.getItem(STORAGE_KEY);
        const res = await fetch('/api/v1/deliverer/me/photo', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            body: form,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw Object.assign(new Error((err as { message?: string }).message ?? 'Erro ao enviar foto'), { status: res.status });
        }
        const data = await res.json() as { photo_url: string };
        if (profile.value) profile.value.photo_url = data.photo_url;
        return data.photo_url;
    }

    async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
        await apiFetch('POST', '/api/v1/deliverer/me/password', {
            current_password: currentPassword,
            password: newPassword,
            password_confirmation: newPassword,
        });
    }

    return { profile, loading, error, statusUpdating, pendingCount, fetchProfile, setStatus, updateProfile, updatePhoto, changePassword };
});
